import { createElement, useContext, type ReactElement } from "react";
import {
  $applyNodeReplacement,
  DecoratorNode,
  type DOMConversionMap,
  type DOMConversionOutput,
  type LexicalNode,
  type NodeKey,
  type SerializedLexicalNode,
  type Spread,
} from "lexical";
import Status from "../UI/Status";
import { VariableContext } from "./VariableContext";

function VariablePill({ variableKey }: { variableKey: string }): ReactElement {
  const { available } = useContext(VariableContext);
  const found = available.find((v) => v.key === variableKey);
  return createElement(Status, {
    as: "span",
    color: "info",
    text: found ? found.label : `{${variableKey}}`,
  });
}

export interface EditorVariable {
  key: string;
  label: string;
}

export type SerializedVariableNode = Spread<
  { variableKey: string; type: "variable"; version: 1 },
  SerializedLexicalNode
>;

export class VariableNode extends DecoratorNode<ReactElement> {
  __variableKey: string;

  static getType(): string {
    return "variable";
  }

  static clone(node: VariableNode): VariableNode {
    return new VariableNode(node.__variableKey, node.__key);
  }

  constructor(variableKey: string, key?: NodeKey) {
    super(key);
    this.__variableKey = variableKey;
  }

  createDOM(): HTMLElement {
    const span = document.createElement("span");
    span.contentEditable = "false";
    return span;
  }

  updateDOM(): false {
    return false;
  }

  decorate(): ReactElement {
    return createElement(VariablePill, { variableKey: this.__variableKey });
  }

  exportDOM(): { element: HTMLElement } {
    const el = document.createElement("span");
    el.setAttribute("data-variable", this.__variableKey);
    el.textContent = `{${this.__variableKey}}`;
    return { element: el };
  }

  static importDOM(): DOMConversionMap | null {
    return {
      span: (node: HTMLElement) => {
        if (!node.hasAttribute("data-variable")) return null;
        return {
          conversion: (el: HTMLElement): DOMConversionOutput => ({
            node: $createVariableNode(el.getAttribute("data-variable") || ""),
          }),
          priority: 1,
        };
      },
    };
  }

  exportJSON(): SerializedVariableNode {
    return { type: "variable", variableKey: this.__variableKey, version: 1 };
  }

  static importJSON(json: SerializedVariableNode): VariableNode {
    return $createVariableNode(json.variableKey);
  }

  isInline(): true {
    return true;
  }

  isKeyboardSelectable(): true {
    return true;
  }

  getTextContent(): string {
    return `{${this.__variableKey}}`;
  }

  getVariableKey(): string {
    return this.__variableKey;
  }
}

export function $createVariableNode(variableKey: string): VariableNode {
  return $applyNodeReplacement(new VariableNode(variableKey));
}

export function $isVariableNode(
  node: LexicalNode | null | undefined,
): node is VariableNode {
  return node instanceof VariableNode;
}
